import { CheckboxCustomEvent, IonSpinner } from '@ionic/react';
import classNames from 'classnames';
import { BasicPost, FullBasicPost } from 'model';
import React, { useCallback, useState } from 'react';
import { useAddPostImageMutation, useUpdateDisplayCommentsMutation } from '../api/PostApi';
import { FormHookReturnType, FormValuesType } from '../hooks/useFormValues';
import { useImageUpload } from '../hooks/useImageUpload';
import { usePasteURL } from '../hooks/usePasteURL';
import { filterInlineImages } from '../util/filterInlineImages';
import { BigImageContainer } from './BigImageContainer';
import { Checkbox } from './Forms/Checkbox';
import { FormInlineImageArea } from './Forms/FormInlineImageArea';
import { FormInput } from './Forms/FormInput';
import { FormTextarea } from './Forms/FormTextarea';
import { TopicSearchBox } from './Forms/TopicSearchBox';
import { IconButton } from './IconButton';
import { LinkPreviewComponent } from './LinkPreviewComponent';
import { IconAdd } from './QuirkIcons';
import { UploadImageButton } from './UploadImageButton';
// styles
import styles from './PostEditFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from './Forms/FormFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';

export const PostEditFields: React.FC<{
	post: FullBasicPost;
	setValues: FormHookReturnType<BasicPost>['setValues'];
	formValues: FormValuesType<BasicPost>;
	setPost: (post: FullBasicPost | undefined | null) => void;
}> = ({ post, setValues, formValues, setPost }) => {
	const [updateDisplayComments] = useUpdateDisplayCommentsMutation();
	const { uploadImage } = useImageUpload(post, useAddPostImageMutation);
	const { onPaste: onPasteSocial, loading: loadingSocial } = usePasteURL<BasicPost>(setValues, 'contents');
	const [titleSelected, setTitleSelected] = useState<boolean>(Boolean(post?.title));
	const onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		onPasteSocial(e);
		uploadImage(e);
	};

	const commentCheck = useCallback(
		async (e: CheckboxCustomEvent) => {
			setPost({ ...post, displayComments: e.target.checked });
			setValues({ displayComments: e.target.checked });
			await updateDisplayComments({ postid: post._id, display: e.target.checked });
		},
		[post, setPost, setValues, updateDisplayComments]
	);

	return (
		<>
			<div className={formStyles.fieldsContainer}>
				{formValues && (
					<>
						{formValues.topic && (
							<TopicSearchBox
								label="Topic"
								nogap={!titleSelected}
								topic={formValues.topic?.value}
								setValues={setValues}
								errors={formValues.topic?.errors}
								testId="editPostTopicField"
							/>
						)}
						<div className={styles.titleFieldContainer} style={{ height: titleSelected ? 100 : 0 }}>
							<FormInput
								id="title"
								setValues={setValues}
								field={formValues.title}
								label="Title"
								testId="editPostTitleField"
							/>
						</div>

						<div className={styles.addTitleButtonContainer} style={{ height: titleSelected ? 0 : 60 }}>
							<IconButton
								icon={<IconAdd />}
								label="Add Title"
								onClick={() => setTitleSelected(!titleSelected)}
								testId="editPostAddTitleButton"
							/>
						</div>

						<div className={loadingSocial ? styles.fieldFade : ''}>
							<FormInlineImageArea
								setValues={setValues}
								content={formValues?.contents?.value}
								document={post}
							>
								<FormTextarea
									id="contents"
									rows={7}
									setValues={setValues}
									field={formValues.contents}
									nogap={!titleSelected}
									label="Content"
									inlineImage
									onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
										if (loadingSocial) e.preventDefault();
									}}
									placeholder="Type your post, paste images or links to embed them"
									onPaste={e => onPaste(e)}
									testId="editPostContentField"
								/>

								<div className={classNames(styles.loading, loadingSocial ? styles.show : '')}>
									<div>Fetching Preview</div>
									<IonSpinner style={{ height: '15px' }} />
								</div>
							</FormInlineImageArea>
						</div>

						{post.images.length > 0 && (
							<div className={postStyles.postImageContainer}>
								<BigImageContainer
									post={post}
									edit={true}
									images={filterInlineImages(formValues?.contents?.value as string, post.images)}
								/>
							</div>
						)}
					</>
				)}
				<LinkPreviewComponent setValues={setValues} linkPreview={formValues.linkPreview?.value} />
				<div className={styles.postFooter}>
					<Checkbox onClick={commentCheck} value={post.displayComments} label="Show Comments" />
					{post.images.length < 6 && <UploadImageButton post={post} />}
				</div>
			</div>
		</>
	);
};
